import React, { useState } from "react";

/**
 * LNL brand mark.
 *
 * The historical external URL (lyceumnorthernluzon.edu.ph/images/.../lnllogo.png)
 * currently returns an HTML error page instead of the PNG, so the previous
 * <img> rendered its alt text ("Lyceur…") in the top-left. We now:
 *   1. try the provided/remote logo first (backwards-compatible), and
 *   2. fall back to an inline SVG monogram if that image fails to load.
 *
 * The inline fallback has no network dependency, so the header can never
 * appear "broken" again.
 */
export default function Logo({
    src,
    size = 44,
    alt = "Lyceum Northern Luzon official seal",
    className = "",
}) {
    const remoteUrl = src || "https://customer-assets-eiarnc6j.emergentagent.net/job_lnl-admissions/artifacts/vbylfelw_Lyceum%20logo.jpg";
    const [failed, setFailed] = useState(false);

    if (failed) {
        return <LogoFallback size={size} className={className} title={alt} />;
    }

    return (
        <img
            src={remoteUrl}
            alt={alt}
            width={size}
            height={size}
            className={className}
            style={{ width: size, height: size, objectFit: "contain" }}
            loading="eager"
            onError={() => setFailed(true)}
            data-testid="lnl-logo"
        />
    );
}

function LogoFallback({ size, className, title }) {
    // Green shield with gold "LNL" wordmark — matches the brand palette.
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 64 64"
            role="img"
            aria-label={title}
            className={className}
            style={{ width: size, height: size }}
            data-testid="lnl-logo"
        >
            <title>{title}</title>
            <defs>
                <linearGradient id="lnl-shield" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#166534" />
                    <stop offset="100%" stopColor="#0F1A11" />
                </linearGradient>
            </defs>
            <path
                d="M32 3 L58 12 V32 C58 46 46 56 32 61 C18 56 6 46 6 32 V12 Z"
                fill="url(#lnl-shield)"
                stroke="#E9C87B"
                strokeWidth="1.5"
            />
            <text
                x="32"
                y="38"
                textAnchor="middle"
                fontFamily="'Plus Jakarta Sans', 'Segoe UI', Arial, sans-serif"
                fontWeight="800"
                fontSize="17"
                letterSpacing="1.5"
                fill="#E9C87B"
            >
                LNL
            </text>
        </svg>
    );
}
